// Components==============
import deleteFeedbackAction from 'actions/feedback/destroy';
import updateProcess from 'actions/process/update';
import destroyProjectGroup from 'actions/project-group/destroy';
import storeProjectGroup from 'actions/project-group/store';
import updateProjectGroup from 'actions/project-group/update';
import destroyProject from 'actions/project/destroy';
import indexProject from 'actions/project/index';
import storeProject from 'actions/project/store';
import updateProject from 'actions/project/update';
import destroyTheme from 'actions/theme/destroy';
import indexTheme from 'actions/theme/index';
import updateTheme from 'actions/theme/update';
import { useAlert } from 'hooks/useAlert';
import { useConfirm } from 'hooks/useConfirm';
import { usePermission } from 'hooks/usePermission';
import { EditableProcessEntity, ProcessGroups } from 'hooks/useProcessGroups';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory } from 'react-router';
import { EvidenceState } from 'typings';
import { getCorrectPhase } from 'utilities/getCorrectPhase';
import { useState } from 'react';
import { ProjectDetailsContextType } from '../ProjectDetails';
// =========================

export type ProjectDetailFunctions = ReturnType<
  typeof useProjectDetailsFunctions
>;

export function useProjectDetailsFunctions({
  theme,
  project,
  userId,
  setIsEditable,
  processGroups,
  setProcessGroups,
  setSaveKey,
  rootProject,
  sparks,
}: ProjectDetailsContextType) {
  const dispatch = useReduxAction();
  const history = useHistory();
  const alert = useAlert();
  const translate = useTranslate();
  const confirm = useConfirm();
  const isTeacher = usePermission(['Docent', 'super-admin']);
  const [isSaving, setIsSaving] = useState(false);

  function handlePublication({
    processIsChecked,
    themeIsChecked,
  }: {
    processIsChecked: boolean;
    themeIsChecked?: boolean;
  }) {
    if (!rootProject?.is_public && processIsChecked) {
      dispatch(
        updateProject({
          id: rootProject?.id,
          editPublicStatus: true,
          is_public: 1,
        })
      );
    }

    if (rootProject?.is_public && !processIsChecked) {
      dispatch(
        updateProject({
          id: rootProject?.id,
          editPublicStatus: true,
          is_public: 0,
        })
      );
    }

    if (!theme?.public && themeIsChecked) {
      dispatch(
        updateTheme({
          id: theme?.id,
          editPublicStatus: true,
          public: 1,
        })
      );
    }
  }

  function handleDelete({
    processIsChecked,
    themeIsChecked,
    selectedUsers,
    historyPush,
  }: {
    processIsChecked: boolean;
    themeIsChecked: boolean;
    selectedUsers: number[];
    historyPush?: boolean;
  }) {
    const id = rootProject?.id;

    // destroy process
    if (processIsChecked && !themeIsChecked) {
      dispatch(
        updateProject({
          id,
          clearProcess: true,
        })
      )
        .then(() =>
          dispatch(
            updateProject({
              id,
              editPublicStatus: true,
              is_public: 0,
            })
          )
        )
        .catch(() => {
          alert(translate('project.errors.delete-failed'));
        });
    }

    // destroy theme
    if (themeIsChecked) {
      const member = rootProject?.members.find((m) => m.user_id === userId);

      const destroyFunction =
        member?.membership === 'admin' && isTeacher
          ? destroyProjectGroup({ id: member.id })
          : destroyProject(id);

      dispatch(destroyFunction)
        .then(() => {
          history.push('/werkplaats/mijn-projecten');
        })
        .catch(() => {
          alert(translate('project.errors.delete-failed'));
        });

      if (theme?.created_by === userId && !theme?.public)
        dispatch(destroyTheme(theme?.id));
    }

    // remove yourself from groups
    if (selectedUsers.length) {
      const promises: any[] = [];

      selectedUsers.forEach((memberId) => {
        promises.push(dispatch(destroyProjectGroup({ id: memberId })));
      });

      Promise.all(promises).then(() => {
        if (!themeIsChecked && !historyPush)
          history.push(`/werkplaats/project-details/${id}`);

        if (historyPush) history.push('/werkplaats/mijn-projecten');

        dispatch(indexProject());
      });
    }
  }

  function handleEvidenceUpload({
    file,
    link,
    processId,
  }: {
    file?: File;
    link?: string;
    processId: number;
  }) {
    const values = {
      uploadNewEvidence: true,
      processId,
      processType: 'App\\ProjectProcess',
    };

    dispatch(
      updateProcess(
        file ? { ...values, file } : { ...values, link, type: 'extern' }
      )
    );
  }

  function handleDeleteEvidenceAndFeedback({
    evidenceId,
    feedback,
  }: EvidenceState) {
    function deleteEvidence(id: number) {
      dispatch(
        updateProcess({
          id,
          destroyEvidence: true,
        })
      ).catch(() => alert(translate('work.error.deleting-evidence')));
    }

    confirm(translate('work.confirm-delete-evidence-and-feedback'), () => {
      feedback?.map((id) => id && dispatch(deleteFeedbackAction(id)));
      setTimeout(() => deleteEvidence(evidenceId), 300);
    });
  }

  // Saves and checks if everything is filled in correctly
  function handleProcessSave(process?: ProcessGroups, nextLocation?: string) {
    if (isSaving) {
      return alert('Het project is nog aan het opslaan', 'Even geduld...');
    }
    setIsSaving(true);
    dispatch(
      updateProject({
        id: project?.id,
        process: process || processGroups,
        check: false,
        sparks: sparks.map((spark) => spark.id),
      })
    )
      .then(() => {
        setIsSaving(false);
        setIsEditable(false);
        setSaveKey((prev) => prev + 1);
        if (nextLocation) history.push(nextLocation);
      })
      .catch(() => {
        setIsSaving(false);
        alert(translate('general.error'));
      });
  }

  // Saves but does not check if everything is filled in due to unknown token
  function handleProcessSaveUnknownToken(
    process?: ProcessGroups,
    nextLocation?: string
  ) {
    console.log(processGroups);
    dispatch(
      updateProject({
        id: project?.id,
        process: process || processGroups,
        check: true,
      })
    )
      .then(() => {
        setIsEditable(false);
        setSaveKey((prev) => prev + 1);
        if (nextLocation) history.push(nextLocation);
      })
      .catch(() => alert(translate('general.error')));
  }

  const handleReorder = ({ source, destination }: DropResult) => {
    const { droppableId } = source;

    if (droppableId === 'phases') {
      // Handle phase drag
      const dragGroup = processGroups.find((g, i) => source.index + 1 === i);

      if (destination && dragGroup)
        return setProcessGroups((prev) => {
          prev.splice(source.index + 1, 1);
          prev.splice(destination.index + 1, 0, dragGroup);

          return [...prev];
        });
    }

    // handle activity drag
    const phaseId = parseInt(source.droppableId);

    return setProcessGroups((prev) =>
      prev.map((group, index) => {
        if (index === phaseId) {
          const dragActivity = group.activities.find(
            (a, i) => source.index === i
          );

          if (destination && dragActivity) {
            group.activities.splice(source.index, 1);
            group.activities.splice(destination.index, 0, dragActivity);

            return { ...group, activities: [...group.activities] };
          }
        }
        return group;
      })
    );
  };

  const handleDeleteActivity = (id?: number, type?: ProcessType) => {
    if (type === 'App\\Phase') {
      const activities = processGroups
        .find((phase) => phase.processId === id)
        ?.activities.filter((a) => a.process_type !== 'App\\Phase');

      const currentIndex = processGroups.findIndex(
        (phase) => phase.processId === id
      );

      const previousIndex = (currentIndex || 0) - 1;

      // Delete phase and move activities to previous phase
      return setProcessGroups((prev) =>
        prev
          .map((phase, index) => {
            if (index === previousIndex && activities?.length)
              return {
                ...phase,
                activities: [...phase.activities, ...activities],
              };

            return phase;
          })
          .filter((phase) => phase.processId !== id)
      );
    }

    setProcessGroups((prev) =>
      prev.map((group) => ({
        ...group,
        activities: group.activities.filter(
          (a) => !(a.processId === id && a.process_type === type)
        ),
      }))
    );
  };

  const handleAddProcessItem = (
    process_type: ProcessType,
    token: ActivityEntity | TrainingEntity | PhaseEntity,
    phaseIndex = 0,
    processIndex = 0
  ) => {
    const processItem: EditableProcessEntity = {
      description: '',
      order: 0,
      id: token.id,
      process_type,
      processId: Math.floor(Math.random() * 10e6),
      finished: 0,
      users: [],
      date: undefined,
      date_type: undefined,
    };

    if (process_type === 'App\\Phase') {
      const newPhase = {
        ...processItem,
        phase: getCorrectPhase(token.id - 1),
        activities: [processItem],
      };

      // can add without splitting phase
      if (
        processGroups[phaseIndex].phase === 'vertrekpunt' ||
        processGroups[phaseIndex].activities.length === processIndex
      )
        return setProcessGroups((prev) => {
          prev.splice(phaseIndex + 1, 0, newPhase);

          return [...prev];
        });

      // split the current phase into two parts
      return setProcessGroups((prev) => {
        // The previous group with all the items that come before the add index
        const previousGroupPhase = prev[phaseIndex];
        const previousGroupActivities = prev[phaseIndex].activities.filter(
          (a, i) => i < processIndex
        );
        const previousGroup = {
          ...previousGroupPhase,
          activities: previousGroupActivities,
        };

        // The new group with the new phase and all the items that came after the original activity within its relative group.
        const newGroupActivities = [
          newPhase,
          ...prev[phaseIndex].activities.filter((a, i) => i > processIndex - 1),
        ];

        const newGroup = {
          ...newPhase,
          activities: newGroupActivities,
        };

        // Combine the previous group with the new group and ad them to the rest of the process
        const newValue: ProcessGroups = [];

        prev.forEach((val, i) => {
          if (i === phaseIndex) {
            newValue.push(previousGroup);
            return newValue.push(newGroup);
          }

          return newValue.push(val);
        });

        return newValue;
      });
    }

    return setProcessGroups((prev) =>
      prev.map((group, i) => {
        if (i === phaseIndex) {
          group.activities.splice(processIndex, 0, processItem);

          return { ...group, activities: [...group.activities] };
        }
        return group;
      })
    );
  };

  const handleSwitchProcessItem = (
    process_type: ProcessType,
    token: ActivityEntity | TrainingEntity | PhaseEntity,
    phaseIndex = 0,
    processIndex = 0,
    onbekend?: boolean
  ) => {
    console.log(onbekend);
    if (process_type === 'App\\Phase') {
      // Switch phase to other phase
      if (processIndex === 1)
        return setProcessGroups((prev) =>
          prev.map((group, index) => {
            if (index === phaseIndex)
              return {
                ...group,
                id: token.id,
                phase: getCorrectPhase(token.id - 1),
                activities: group.activities.map((e, i) =>
                  i === 0 ? { ...e, id: token.id } : e
                ),
              };

            return group;
          })
        );

      // Switch activity to phase and add a new phase group
      return setProcessGroups((prev) => {
        // The previous group with all the items that come before the change index
        const previousGroupPhase = prev[phaseIndex];
        const previousGroupActivities = prev[phaseIndex].activities.filter(
          (a, i) => i < processIndex - 1
        );
        const previousGroup = {
          ...previousGroupPhase,
          activities: previousGroupActivities,
        };

        // The new group with the new phase and all the items that came after the original activity within its relative group.
        const newGroupPhase = {
          ...prev[phaseIndex],
          id: token.id,
          phase: getCorrectPhase(token.id - 1),
          activities: undefined,
        };

        const newGroupActivities = [
          newGroupPhase,
          ...prev[phaseIndex].activities.filter((a, i) => i > processIndex - 1),
        ];

        const newGroup = {
          ...newGroupPhase,
          activities: newGroupActivities,
        };

        // Combine the previous group with the new group and ad them to the rest of the process
        const newValue: ProcessGroups = [];

        prev.forEach((val, i) => {
          if (i === phaseIndex) {
            newValue.push(previousGroup);
            return newValue.push(newGroup);
          }

          return newValue.push(val);
        });

        return newValue;
      });
    }

    return setProcessGroups((prev) =>
      prev.map((group, index) => {
        if (index === phaseIndex) {
          if (onbekend) {
            console.log('onbekend token switch');
            return {
              ...group,
              activities: group.activities.map((e, i) =>
                i + 1 === processIndex
                  ? { ...e, description: '', id: token.id, process_type }
                  : e
              ),
            };
          }
          console.log('normale token switch');
          return {
            ...group,
            activities: group.activities.map((e, i) =>
              i + 1 === processIndex ? { ...e, id: token.id, process_type } : e
            ),
          };
        }
        return group;
      })
    );
  };

  const handleAddGroupMember = (userIds: number[]) => {
    userIds.forEach((id) =>
      dispatch(
        storeProjectGroup({ project_id: rootProject?.id, user_id: id })
      ).then(() => dispatch(indexProject()))
    );
  };

  const handleAddAdmin = (id: number) => {
    dispatch(updateProjectGroup({ id, membership: 'admin' })).then(() =>
      dispatch(indexProject())
    );
  };

  const handleDeleteAdmin = (id: number) => {
    dispatch(updateProjectGroup({ id, membership: 'member' })).then(() =>
      dispatch(indexProject())
    );
  };

  const handleDeleteMember = (id: number) => {
    dispatch(destroyProjectGroup({ id })).then(() => dispatch(indexProject()));
  };

  const handleOwnerTransfer = (id: number) => {
    dispatch(
      destroyProjectGroup({ transfer_to_id: id, project_id: rootProject?.id })
    )
      .then(() => dispatch(indexProject()))
      .then(() => history.push('/werkplaats/mijn-projecten'));
  };

  const handleLeaveGroup = () => {
    const id = project?.members.find((m) => m.user_id === userId)?.id;

    dispatch(destroyProjectGroup({ id })).then(() =>
      dispatch(indexProject()).then(() =>
        history.push('/werkplaats/mijn-projecten')
      )
    );
  };

  const handleProjectAndThemeDuplication = () => {
    dispatch(
      storeProject({
        duplicateProjectAndTheme: true,
        id: rootProject?.id,
      })
    )
      .then((newProject: ProjectEntity) =>
        dispatch(indexTheme()).then(() =>
          history.push(`/werkplaats/project-details/${newProject.id}`)
        )
      )
      .catch(() => {
        alert(translate('project.errors.could-not-add-project'));
      });
  };

  const handleExport = () => {};

  return {
    handleProcessSave,
    handleProcessSaveUnknownToken,
    handleDelete,
    handleDeleteEvidenceAndFeedback,
    handlePublication,
    handleEvidenceUpload,
    handleReorder,
    handleDeleteActivity,
    handleAddProcessItem,
    handleSwitchProcessItem,
    handleAddGroupMember,
    handleAddAdmin,
    handleDeleteAdmin,
    handleDeleteMember,
    handleOwnerTransfer,
    handleLeaveGroup,
    handleProjectAndThemeDuplication,
    handleExport,
  };
}
